import Search, { Option, customSelectStyles } from 'components/elements/Search';
import ErrorsComponent from 'shared/Errors/components/Errors';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import Overlay from 'shared/Overlay';
import { OverlayProps } from 'shared/Overlay/components/Overlay';
import OverlayButton from 'shared/Overlay/components/OverlayButton';
import useStores from 'utils/useStores';
import ProductionOrderStore from '../stores/ProductionOrderStore';
import ProductionOrder from '../types/ProductionOrder';

// Render the menu in a portal so it is not clipped by the Overlay modal, and let
// its width follow the control instead of the fixed 28em from customSelectStyles.
const markSelectStyles = {
  ...customSelectStyles,
  menu: (provided: any) => ({ ...provided }),
  menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
};

function Button(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <button className='button button--small button--required' {...props}>
      Add Mark
    </button>
  );
}

export interface AddMarkOverlayProps extends Pick<OverlayProps, 'onSecondaryClick'> {
  marks: Option[];
}

function AddMarkOverlay({ marks, ...props }: AddMarkOverlayProps) {
  const { productionOrderStore } =
    useStores<{ productionOrderStore: ProductionOrderStore }>();
  const [markId, setMarkId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleOnPrimaryClick = async () => {
    setSubmitting(true);
    setErrors([]);

    try {
      const response = await productionOrderStore.saveMark(markId);

      if (response?.success) {
        window.location.reload();
      } else {
        setErrors(
          response?.errors?.length
            ? response.errors
            : [I18n.translate('milling.production_orders.add_mark.error')]
        );
        setSubmitting(false);
      }
    } catch (e) {
      setErrors([I18n.translate('milling.production_orders.add_mark.error')]);
      setSubmitting(false);
    }
  };

  return (
    <Overlay
      title='Add Mark'
      primaryText={I18n.translate('buttons.save')}
      onPrimaryClick={handleOnPrimaryClick}
      primaryDisabled={!markId || submitting}
      secondaryText={I18n.translate('buttons.close')}
      {...props}
    >
      <Search
        options={marks}
        onChange={(selected: Option) => setMarkId(String(selected.value))}
        onClear={() => setMarkId('')}
        placeholder={I18n.translate(
          'milling.production_orders.add_mark.select_placeholder'
        )}
        noOptionsMessage={() =>
          I18n.translate('milling.production_orders.add_mark.no_options')
        }
        styles={markSelectStyles}
        menuPortalTarget={document.body}
        menuPlacement='auto'
      />

      <ErrorsComponent errors={errors} />
    </Overlay>
  );
}

export interface AddMarkProps {
  productionOrder: ProductionOrder;
}

function AddMark({ productionOrder }: AddMarkProps) {
  const { productionOrderStore } =
    useStores<{ productionOrderStore: ProductionOrderStore }>();

  return (
    <OverlayButton
      button={Button}
      onClick={() => productionOrderStore.selectProductionOrder(productionOrder)}
      overlay={({ closeOverlay }) => (
        <AddMarkOverlay
          onSecondaryClick={closeOverlay}
          marks={productionOrderStore.markOptions}
        />
      )}
    />
  );
}

export default observer(AddMark);
